import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';

const ContractModal = ({ contract, mode, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showNewTenantForm, setShowNewTenantForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: 임대사업자/건물/임차인, 2: 공실선택, 3: 계약정보
  const [selectedPropertyOwner, setSelectedPropertyOwner] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [newTenantData, setNewTenantData] = useState({
    name: '',
    phone: '',
    email: '',
    resident_number: '',
    emergency_contact: '',
    emergency_phone: '',
    occupation: '',
    workplace: '',
    workplace_phone: '',
    notes: ''
  });

  // ref를 사용한 안정적인 입력 처리
  const inputRefs = useRef({});

  // 사용자 정보 가져오기 (RMC 사용자 확인용)
  const { data: userInfo } = useQuery(['user-info'], () =>
    api.get('/auth/me'),
    {
      onSuccess: (data) => {
        setCurrentUser(data);
      }
    }
  );

  const isRMCUser = ['rental_management_company', 'rmc_admin', 'rmc_staff'].includes(userInfo?.role);

  // 임대사업자 목록 가져오기 (RMC 사용자용)
  const { data: propertyOwners } = useQuery(
    ['property-owners', userInfo?.role],
    () => api.get('/property-owners'),
    {
      enabled: isRMCUser,
      staleTime: 5 * 60 * 1000, // 5분
      cacheTime: 10 * 60 * 1000, // 10분
    }
  );

  // 건물 목록 조회
  const { data: buildingsData } = useQuery(
    ['contract-buildings', selectedPropertyOwner, userInfo?.role],
    () => {
      const url = selectedPropertyOwner
        ? `/buildings?property_owner_id=${selectedPropertyOwner}`
        : '/buildings';
      return api.get(url);
    },
    { enabled: !isViewMode && !!userInfo && (!isRMCUser || !!selectedPropertyOwner) }
  );

  // 세대 목록 조회 (사용자가 접근 가능한 모든 세대)
  const { data: unitsData } = useQuery(
    ['units', selectedPropertyOwner, selectedBuilding],
    () => {
      const url = selectedPropertyOwner
        ? `/buildings/units/all?property_owner_id=${selectedPropertyOwner}`
        : '/buildings/units/all';
      return api.get(url);
    },
    { enabled: !isViewMode && !!selectedBuilding }
  );

  // 임차인 목록 조회
  const { data: tenantsData } = useQuery(
    ['tenants', selectedPropertyOwner],
    () => {
      const url = selectedPropertyOwner
        ? `/tenants?property_owner_id=${selectedPropertyOwner}`
        : '/tenants';
      return api.get(url);
    },
    { enabled: !isViewMode }
  );

  const buildings = Array.isArray(buildingsData)
    ? buildingsData
    : buildingsData?.buildings || buildingsData?.data || [];
  const units = (unitsData?.units || []).filter(
    (unit) => !selectedBuilding || String(unit.building_id) === String(selectedBuilding)
  );
  const tenants = tenantsData?.tenants || [];

  // 수정 모드 또는 보기 모드일 때 contract 데이터를 만원 단위로 변환
  const getFormDefaultValues = () => {
    if (contract && (isEditMode || isViewMode)) {
      console.log('🔍 getFormDefaultValues - contract:', contract);
      console.log('🔍 getFormDefaultValues - isEditMode:', isEditMode);
      console.log('🔍 getFormDefaultValues - isViewMode:', isViewMode);
      const formValues = {
        ...contract,
        // 원 단위를 만원 단위로 변환 (입력 필드가 만원 단위이므로)
        monthly_rent: contract.monthly_rent ? Math.round(contract.monthly_rent / 10000) : '',
        deposit: contract.deposit ? Math.round(contract.deposit / 10000) : '',
        // 날짜 필드는 문자열로 변환
        start_date: contract.start_date ? (typeof contract.start_date === 'string' ? contract.start_date.split('T')[0] : contract.start_date) : '',
        end_date: contract.end_date ? (typeof contract.end_date === 'string' ? contract.end_date.split('T')[0] : contract.end_date) : '',
        move_in_date: contract.move_in_date ? (typeof contract.move_in_date === 'string' ? contract.move_in_date.split('T')[0] : contract.move_in_date) : '',
        move_out_date: contract.move_out_date ? (typeof contract.move_out_date === 'string' ? contract.move_out_date.split('T')[0] : contract.move_out_date) : '',
        renewal_date: contract.renewal_date ? (typeof contract.renewal_date === 'string' ? contract.renewal_date.split('T')[0] : contract.renewal_date) : '',
        contract_signed_date: contract.contract_signed_date ? (typeof contract.contract_signed_date === 'string' ? contract.contract_signed_date.split('T')[0] : contract.contract_signed_date) : '',
      };
      console.log('🔍 getFormDefaultValues - formValues:', formValues);
      console.log('🔍 monthly_rent 변환:', contract.monthly_rent, '->', formValues.monthly_rent);
      console.log('🔍 deposit 변환:', contract.deposit, '->', formValues.deposit);
      return formValues;
    }
    return {
      unit_id: '',
      tenant_id: '',
      contract_number: '',
      contract_type: 'monthly',
      contract_category: 'residential',
      start_date: '',
      end_date: '',
      move_in_date: '',
      move_out_date: '',
      renewal_date: '',
      notice_period: 30,
      monthly_rent: '',
      monthly_rent_vat_type: 'exempt',
      deposit: '',
      maintenance_fee: '',
      maintenance_fee_vat_type: 'exempt',
      parking_fee: '',
      parking_fee_vat_type: 'exempt',
      due_date: 1,
      late_fee_rate: 0.05,
      grace_period: 5,
      max_occupants: '',
      pet_allowed: false,
      smoking_allowed: false,
      parking_spaces: 0,
      utility_included: false,
      status: 'active',
      contract_signed_date: '',
      contract_verified: false,
      notes: ''
    };
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
    control
  } = useForm({
    defaultValues: {
      unit_id: '',
      tenant_id: '',
      contract_number: '',
      contract_type: 'monthly',
      contract_category: 'residential',
      start_date: '',
      end_date: '',
      move_in_date: '',
      move_out_date: '',
      renewal_date: '',
      notice_period: 30,
      monthly_rent: '',
      monthly_rent_vat_type: 'exempt',
      deposit: '',
      maintenance_fee: '',
      maintenance_fee_vat_type: 'exempt',
      parking_fee: '',
      parking_fee_vat_type: 'exempt',
      due_date: 1,
      late_fee_rate: 0.05,
      grace_period: 5,
      max_occupants: '',
      pet_allowed: false,
      smoking_allowed: false,
      parking_spaces: 0,
      utility_included: false,
      status: 'active',
      contract_signed_date: '',
      contract_verified: false,
      notes: ''
    }
  });

  const watchedUnitId = watch('unit_id');
  const watchedTenantId = watch('tenant_id');

  useEffect(() => {
    setSelectedBuilding('');
    setValue('unit_id', '');
    setSelectedUnit(null);
  }, [selectedPropertyOwner, setValue]);

  // 수정 모드 또는 보기 모드일 때 contract prop이 변경되면 폼 업데이트
  useEffect(() => {
    if (contract && (isEditMode || isViewMode)) {
      console.log('🔍 계약 모드 - contract prop 변경됨, 폼 업데이트');
      console.log('🔍 isEditMode:', isEditMode);
      console.log('🔍 isViewMode:', isViewMode);
      console.log('🔍 contract:', contract);
      console.log('🔍 contract의 부가세 필드들:', {
        monthly_rent_vat_included: contract.monthly_rent_vat_included,
        monthly_rent_vat_exempt: contract.monthly_rent_vat_exempt,
        monthly_rent_vat_separate: contract.monthly_rent_vat_separate,
        maintenance_fee_vat_included: contract.maintenance_fee_vat_included,
        maintenance_fee_vat_exempt: contract.maintenance_fee_vat_exempt,
        maintenance_fee_vat_separate: contract.maintenance_fee_vat_separate,
        parking_fee_vat_included: contract.parking_fee_vat_included,
        parking_fee_vat_exempt: contract.parking_fee_vat_exempt,
        parking_fee_vat_separate: contract.parking_fee_vat_separate,
      });
      
      // 날짜 변환 헬퍼 함수
      const formatDate = (dateValue) => {
        if (!dateValue) return '';
        if (typeof dateValue === 'string') {
          return dateValue.split('T')[0];
        }
        if (dateValue instanceof Date) {
          return dateValue.toISOString().split('T')[0];
        }
        return dateValue;
      };
      
      // 부가세 타입 결정 함수
      const getVatType = (contract, fieldPrefix) => {
        const exemptKey = `${fieldPrefix}_vat_exempt`;
        const separateKey = `${fieldPrefix}_vat_separate`;
        const includedKey = `${fieldPrefix}_vat_included`;
        
        const exempt = contract[exemptKey];
        const separate = contract[separateKey];
        const included = contract[includedKey];
        
        console.log(`🔍 getVatType - ${fieldPrefix}:`, {
          exempt,
          separate,
          included,
          exemptType: typeof exempt,
          separateType: typeof separate,
          includedType: typeof included,
          exemptKey,
          separateKey,
          includedKey,
          contractKeys: Object.keys(contract).filter(k => k.includes('vat'))
        });
        
        // 우선순위: exempt > separate > included
        // 명시적으로 true인 경우만 체크 (null, undefined, false는 제외)
        if (exempt === true || exempt === 1 || exempt === 'true') {
          console.log(`  -> exempt (값: ${exempt})`);
          return 'exempt';
        }
        if (separate === true || separate === 1 || separate === 'true') {
          console.log(`  -> separate (값: ${separate})`);
          return 'separate';
        }
        // included가 true이거나 모두 false/null인 경우 included
        // included가 false여도 exempt와 separate가 false면 included가 기본값
        if (included === true || included === 1 || included === 'true') {
          console.log(`  -> included (값: ${included})`);
          return 'included';
        }
        // exempt와 separate가 모두 false/null이면 included가 기본값
        // (included가 undefined/null이어도 기본값은 included)
        // included가 명시적으로 false가 아니면 included로 간주
        if (included !== false && included !== 0 && included !== 'false') {
          console.log(`  -> included (기본값 또는 명시적, exempt: ${exempt}, separate: ${separate}, included: ${included})`);
          return 'included';
        }
        // 모든 값이 false/null이면 included가 기본값
        console.log(`  -> included (최종 기본값, exempt: ${exempt}, separate: ${separate}, included: ${included})`);
        return 'included';
        // 기본값은 included
        console.log(`  -> included (최종 기본값, exempt: ${exempt}, separate: ${separate}, included: ${included})`);
        return 'included';
      };
      
      // 계약기간 계산 (개월 수) - 더 정확한 계산
      const calculateContractPeriod = () => {
        if (contract.start_date && contract.end_date) {
          const startStr = formatDate(contract.start_date);
          const endStr = formatDate(contract.end_date);
          console.log('🔍 계약기간 계산:', startStr, '~', endStr);
          
          if (startStr && endStr) {
            const start = new Date(startStr);
            const end = new Date(endStr);
            
            // 날짜가 유효한지 확인
            if (isNaN(start.getTime()) || isNaN(end.getTime())) {
              console.log('  -> 날짜 파싱 실패');
              return '';
            }
            
            // 정확한 개월 수 계산
            // 예: 2024-01-05 ~ 2026-01-04 = 24개월 (정확히 2년 전날이므로 24개월)
            // 예: 2024-01-05 ~ 2025-01-04 = 12개월 (정확히 1년 전날이므로 12개월)
            // 예: 2024-01-05 ~ 2025-01-05 = 12개월 (정확히 1년이므로 12개월)
            let months = (end.getFullYear() - start.getFullYear()) * 12;
            months += end.getMonth() - start.getMonth();
            
            // 일자는 고려하지 않음 (계약기간은 년/월 기준)
            // 종료일이 시작일의 전날이면 이미 정확한 개월 수가 계산됨
            
            console.log('  -> 계산된 개월 수:', months);
            console.log('  -> 시작일:', start.toISOString().split('T')[0]);
            console.log('  -> 종료일:', end.toISOString().split('T')[0]);
            console.log('  -> 시작일 일자:', start.getDate());
            console.log('  -> 종료일 일자:', end.getDate());
            return months > 0 ? months : '';
          }
        }
        return '';
      };
      
      // 계약기간은 먼저 계산
      const contractPeriod = calculateContractPeriod();
      
      const formValues = {
        // 기본 정보 (명시적으로 설정하여 contract 객체의 다른 필드가 덮어쓰지 않도록)
        contract_category: contract.contract_category || 'residential',
        contract_type: contract.contract_type || 'monthly',
        contract_number: contract.contract_number || '',
        unit_id: contract.unit_id || contract.unit?.id || '',
        tenant_id: contract.tenant_id || contract.tenant?.id || '',
        // 원 단위를 만원 단위로 변환 (입력 필드가 만원 단위이므로)
        monthly_rent: contract.monthly_rent ? Math.round(contract.monthly_rent / 10000) : '',
        deposit: contract.deposit ? Math.round(contract.deposit / 10000) : '',
        // 날짜 필드는 문자열로 변환
        start_date: formatDate(contract.start_date),
        end_date: formatDate(contract.end_date),
        move_in_date: formatDate(contract.move_in_date),
        move_out_date: formatDate(contract.move_out_date),
        renewal_date: formatDate(contract.renewal_date),
        contract_signed_date: formatDate(contract.contract_signed_date),
        // 계약기간
        contract_period: contractPeriod || '',
        // 숫자 필드들
        maintenance_fee: contract.maintenance_fee || '',
        parking_fee: contract.parking_fee || '',
        due_date: contract.due_date || 1,
        late_fee_rate: contract.late_fee_rate || 0.05,
        grace_period: contract.grace_period || 5,
        notice_period: contract.notice_period || 30,
        max_occupants: contract.max_occupants || '',
        parking_spaces: contract.parking_spaces || 0,
        // 부가세 타입 필드들 (명시적으로 설정)
        monthly_rent_vat_type: getVatType(contract, 'monthly_rent'),
        maintenance_fee_vat_type: getVatType(contract, 'maintenance_fee'),
        parking_fee_vat_type: getVatType(contract, 'parking_fee'),
        // 불린 필드들
        pet_allowed: contract.pet_allowed || false,
        smoking_allowed: contract.smoking_allowed || false,
        utility_included: contract.utility_included || false,
        contract_verified: contract.contract_verified || false,
        // 기타 필드들
        notes: contract.notes || '',
        status: contract.status || 'active',
      };
      
      console.log('🔍 formValues:', formValues);
      console.log('🔍 monthly_rent 변환:', contract.monthly_rent, '->', formValues.monthly_rent);
      console.log('🔍 deposit 변환:', contract.deposit, '->', formValues.deposit);
      console.log('🔍 contract_category:', formValues.contract_category);
      console.log('🔍 monthly_rent_vat_type:', formValues.monthly_rent_vat_type);
      console.log('🔍 maintenance_fee_vat_type:', formValues.maintenance_fee_vat_type);
      console.log('🔍 parking_fee_vat_type:', formValues.parking_fee_vat_type);
      console.log('🔍 contract_period:', formValues.contract_period);
      
      console.log('🔍 reset 호출 전 formValues:', formValues);
      reset(formValues, {
        keepDefaultValues: false,
        keepValues: false
      });
      
      // reset 후 부가세 타입 필드를 명시적으로 설정
      setTimeout(() => {
        if (formValues.monthly_rent_vat_type) {
          setValue('monthly_rent_vat_type', formValues.monthly_rent_vat_type);
        }
        if (formValues.maintenance_fee_vat_type) {
          setValue('maintenance_fee_vat_type', formValues.maintenance_fee_vat_type);
        }
        if (formValues.parking_fee_vat_type) {
          setValue('parking_fee_vat_type', formValues.parking_fee_vat_type);
        }
        
        const currentValues = getValues();
        console.log('🔍 reset 후 전체 값:', currentValues);
        console.log('🔍 reset 후 monthly_rent_vat_type:', currentValues.monthly_rent_vat_type);
        console.log('🔍 reset 후 maintenance_fee_vat_type:', currentValues.maintenance_fee_vat_type);
        console.log('🔍 reset 후 parking_fee_vat_type:', currentValues.parking_fee_vat_type);
      }, 100);
    } else if (!contract) {
      // contract가 없으면 폼 초기화
      reset({
        unit_id: '',
        tenant_id: '',
        contract_number: '',
        contract_type: 'monthly',
        contract_category: 'residential',
        start_date: '',
        end_date: '',
        move_in_date: '',
        move_out_date: '',
        renewal_date: '',
        notice_period: 30,
        monthly_rent: '',
        monthly_rent_vat_type: 'exempt',
        deposit: '',
        maintenance_fee: '',
        maintenance_fee_vat_type: 'exempt',
        parking_fee: '',
        parking_fee_vat_type: 'exempt',
        due_date: 1,
        late_fee_rate: 0.05,
        grace_period: 5,
        max_occupants: '',
        pet_allowed: false,
        smoking_allowed: false,
        parking_spaces: 0,
        utility_included: false,
        status: 'active',
        contract_signed_date: '',
        contract_verified: false,
        notes: ''
      });
    }
  }, [contract, isEditMode, isViewMode, reset]); // contract 전체를 dependency로 사용



  // 계약기간 변경 시 자동으로 종료일 계산
  const handleContractPeriodChange = (months) => {
    if (months && watch('start_date')) {
      const startDate = new Date(watch('start_date'));
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + parseInt(months));

      // 계약종료일 설정 (계약일 기준, 종료일 하루 전)
      const contractEndDate = new Date(endDate);
      contractEndDate.setDate(contractEndDate.getDate() - 1);

      setValue('end_date', contractEndDate.toISOString().split('T')[0]);

    }
  };

  // 계약시작일 변경 시에도 자동 계산
  const handleStartDateChange = (e) => {
    const startDate = e.target.value;
    const contractPeriod = document.querySelector('input[type="number"][min="1"][max="120"]')?.value;

    if (startDate && contractPeriod) {
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + parseInt(contractPeriod));

      // 계약종료일 설정 (계약일 기준, 종료일 하루 전)
      const contractEndDate = new Date(endDate);
      contractEndDate.setDate(contractEndDate.getDate() - 1);

      setValue('end_date', contractEndDate.toISOString().split('T')[0]);

    }
  };

  // 입주일 변경 시 퇴주예정일 계산
  const handleMoveInDateChange = (e) => {
    const moveInDate = e.target.value;
    const contractPeriod = document.querySelector('input[type="number"][min="1"][max="120"]')?.value;

    if (moveInDate && contractPeriod) {
      const endDate = new Date(moveInDate);
      endDate.setMonth(endDate.getMonth() + parseInt(contractPeriod));

      // 퇴주예정일 설정 (입주일 기준, 종료일 하루 전)
      const moveOutDate = new Date(endDate);
      moveOutDate.setDate(moveOutDate.getDate() - 1);

      setValue('move_out_date', moveOutDate.toISOString().split('T')[0]);

    }
  };

  // 세대 선택 시 기준금액 자동 설정
  useEffect(() => {
    if (watchedUnitId && units.length > 0) {
      const unit = units.find(u => u.id === parseInt(watchedUnitId));
      if (unit) {
        setSelectedUnit(unit);
        if (!isEditMode) {
          setValue('monthly_rent', unit.standard_monthly_rent || '');
          setValue('deposit', unit.standard_deposit || '');
          setValue('maintenance_fee', unit.standard_maintenance_fee || '');
        }
      }
    }
  }, [watchedUnitId, units, setValue, isEditMode]);

  // 임차인 선택 시 정보 표시
  useEffect(() => {
    if (watchedTenantId && tenants.length > 0) {
      const tenant = tenants.find(t => t.id === parseInt(watchedTenantId));
      if (tenant) {
        setSelectedTenant(tenant);
      }
    }
  }, [watchedTenantId, tenants]);

  // 계약 추가
  const createContractMutation = useMutation(
    (data) => api.post('/contracts', data),
    {
      onSuccess: (response) => {
        toast.success('계약이 성공적으로 등록되었습니다.');
        onSuccess();
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message ||
          error.response?.data?.error ||
          '계약 등록에 실패했습니다.';
        toast.error(errorMessage);
        console.error('계약 등록 오류:', error.response?.data);
      }
    }
  );

  // 계약 수정
  const updateContractMutation = useMutation(
    (data) => {
      console.log('🔍 updateContractMutation 호출');
      console.log('🔍 contract.id:', contract?.id);
      console.log('🔍 전송할 데이터:', data);
      return api.put(`/contracts/${contract.id}`, data);
    },
    {
      onSuccess: (response) => {
        console.log('✅ 계약 수정 성공:', response);
        // 쿼리 캐시 무효화
        queryClient.invalidateQueries('contracts');
        queryClient.invalidateQueries(['contracts', contract?.id]);
        // 성공 메시지 표시
        toast.success('계약 정보가 성공적으로 수정되었습니다.', {
          position: "top-right",
          autoClose: 3000,
        });
        // 모달 닫기 및 콜백 실행 (약간의 지연을 두어 메시지가 보이도록)
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 100);
      },
      onError: (error) => {
        console.error('❌ 계약 수정 오류:', error);
        console.error('❌ 오류 응답:', error.response?.data);
        const errorMessage = error.response?.data?.message ||
          error.response?.data?.error ||
          '계약 수정에 실패했습니다.';
        toast.error(errorMessage);
      }
    }
  );

  const onSubmit = (data) => {
    console.log('🔍 onSubmit 호출됨');
    console.log('🔍 isEditMode:', isEditMode);
    console.log('🔍 contract:', contract);
    console.log('🔍 data:', data);
    
    // data에 부가세 타입 필드가 없으면 getValues()로 가져오기
    const formValues = getValues();
    const monthlyRentVatType = data.monthly_rent_vat_type || formValues.monthly_rent_vat_type || 'exempt';
    const maintenanceFeeVatType = data.maintenance_fee_vat_type || formValues.maintenance_fee_vat_type || 'exempt';
    const parkingFeeVatType = data.parking_fee_vat_type || formValues.parking_fee_vat_type || 'exempt';
    
    console.log('🔍 부가세 타입 필드들:', {
      data_monthly_rent_vat_type: data.monthly_rent_vat_type,
      data_maintenance_fee_vat_type: data.maintenance_fee_vat_type,
      data_parking_fee_vat_type: data.parking_fee_vat_type,
      formValues_monthly_rent_vat_type: formValues.monthly_rent_vat_type,
      formValues_maintenance_fee_vat_type: formValues.maintenance_fee_vat_type,
      formValues_parking_fee_vat_type: formValues.parking_fee_vat_type,
      최종_monthly_rent_vat_type: monthlyRentVatType,
      최종_maintenance_fee_vat_type: maintenanceFeeVatType,
      최종_parking_fee_vat_type: parkingFeeVatType
    });

    // 수정 모드일 때 contract가 없으면 오류
    if (isEditMode && !contract) {
      toast.error('계약 정보를 찾을 수 없습니다.');
      console.error('❌ 수정 모드인데 contract가 없습니다.');
      return;
    }

    // 새로운 임차인과 계약을 함께 등록하는 경우
    if (showNewTenantForm) {
      if (!newTenantData.name || !newTenantData.phone || !newTenantData.email) {
        toast.error('새 임차인의 필수 정보(이름, 전화번호, 이메일)를 모두 입력해주세요.');
        return;
      }

      // 계약 필수 필드 검증
      if (isRMCUser && !selectedPropertyOwner) {
        toast.error('임대사업자를 선택해주세요.');
        return;
      }
      if (!selectedBuilding) {
        toast.error('건물을 선택해주세요.');
        return;
      }
      if (!data.unit_id) {
        toast.error('세대를 선택해주세요.');
        return;
      }
      if (!data.monthly_rent) {
        toast.error('월세를 입력해주세요.');
        return;
      }
      if (!data.deposit) {
        toast.error('보증금을 입력해주세요.');
        return;
      }
      if (!data.contract_type) {
        toast.error('계약유형을 선택해주세요.');
        return;
      }
      if (!data.start_date) {
        toast.error('계약시작일을 입력해주세요.');
        return;
      }
      if (!data.end_date) {
        toast.error('계약종료일을 입력해주세요.');
        return;
      }

      const formattedData = {
        ...data,
        unit_id: parseInt(data.unit_id),
        property_owner_id: isRMCUser ? parseInt(selectedPropertyOwner) : undefined,
        monthly_rent: parseInt(data.monthly_rent),
        deposit: parseInt(data.deposit),
        maintenance_fee: data.maintenance_fee ? parseInt(data.maintenance_fee) : null,
        parking_fee: data.parking_fee ? parseInt(data.parking_fee) : null,
        // 빈 값들을 null로 변환
        renewal_date: data.renewal_date || null,
        contract_signed_date: data.contract_signed_date || null,
        max_occupants: data.max_occupants ? parseInt(data.max_occupants) : null,
        parking_spaces: data.parking_spaces ? parseInt(data.parking_spaces) : null,
        due_date: data.due_date ? parseInt(data.due_date) : null,
        late_fee_rate: data.late_fee_rate ? parseFloat(data.late_fee_rate) : null,
        grace_period: data.grace_period ? parseInt(data.grace_period) : null,
        notice_period: data.notice_period ? parseInt(data.notice_period) : null,
        // 불필요한 필드들 제거
        monthly_rent_vat_included: undefined,
        monthly_rent_vat_exempt: undefined,
        maintenance_fee_vat_included: undefined,
        maintenance_fee_vat_exempt: undefined,
        parking_fee_vat_included: undefined,
        parking_fee_vat_exempt: undefined,
        utility_deposit: undefined,
        tenant_info: {
          name: newTenantData.name,
          phone: newTenantData.phone,
          email: newTenantData.email,
          resident_number: newTenantData.resident_number || null,
          emergency_contact: newTenantData.emergency_contact || null,
          emergency_phone: newTenantData.emergency_phone || null,
          occupation: newTenantData.occupation || null,
          workplace: newTenantData.workplace || null,
          workplace_phone: newTenantData.workplace_phone || null,
          notes: newTenantData.notes || null,
          // 추가 필드들
          household_members: newTenantData.household_members ? parseInt(newTenantData.household_members) : null,
          pet_ownership: newTenantData.pet_ownership || false,
          special_agreement: newTenantData.special_agreement || false,
          vehicle_number: newTenantData.vehicle_number || null
        }
      };

      // 새 임차인 등록 시 tenant_id 제거
      delete formattedData.tenant_id;
      delete formattedData.contract_period;

      createContractMutation.mutate(formattedData);
      return;
    }

    // 수정 모드가 아닐 때만 필수 필드 검증
    if (!isEditMode) {
      // 기존 임차인으로 계약 등록하는 경우
      if (!data.tenant_id) {
        toast.error('임차인을 선택해주세요.');
        return;
      }

      // 계약 필수 필드 검증
      if (isRMCUser && !selectedPropertyOwner) {
        toast.error('임대사업자를 선택해주세요.');
        return;
      }
      if (!selectedBuilding) {
        toast.error('건물을 선택해주세요.');
        return;
      }
      if (!data.unit_id) {
        toast.error('세대를 선택해주세요.');
        return;
      }
      if (!data.monthly_rent) {
        toast.error('월세를 입력해주세요.');
        return;
      }
      if (!data.deposit) {
        toast.error('보증금을 입력해주세요.');
        return;
      }
      if (!data.contract_type) {
        toast.error('계약유형을 선택해주세요.');
        return;
      }
      if (!data.start_date) {
        toast.error('계약시작일을 입력해주세요.');
        return;
      }
      if (!data.end_date) {
        toast.error('계약종료일을 입력해주세요.');
        return;
      }
    }

    // 수정 모드일 때는 변경된 필드만 전송
    // ContractSchema에 정의되지 않은 필드들 제거 (unit, id, payment_account, created_at, tenant, updated_at 등)
    const excludedFields = ['id', 'unit', 'tenant', 'payment_account', 'created_at', 'updated_at', 
                            'monthly_rent_vat_type', 'maintenance_fee_vat_type', 'parking_fee_vat_type',
                            'utility_deposit', 'contract_period'];
    
    const formattedData = {};
    
    // 필수 필드들 (값이 있으면 포함)
    if (data.unit_id) formattedData.unit_id = parseInt(data.unit_id);
    if (data.tenant_id) formattedData.tenant_id = parseInt(data.tenant_id);
    if (isRMCUser && selectedPropertyOwner) {
      formattedData.property_owner_id = parseInt(selectedPropertyOwner);
    }
    // 숫자 필드는 값이 있거나 0일 때도 포함 (0은 falsy이므로 명시적으로 체크)
    // 입력 필드가 만원 단위이므로 원 단위로 변환
    if (data.monthly_rent !== undefined && data.monthly_rent !== null && data.monthly_rent !== '') {
      const monthlyRentValue = typeof data.monthly_rent === 'string' 
        ? parseInt(data.monthly_rent.replace(/,/g, ''), 10) 
        : parseInt(data.monthly_rent, 10);
      if (!isNaN(monthlyRentValue)) {
        formattedData.monthly_rent = monthlyRentValue * 10000; // 만원 단위를 원 단위로 변환
      }
    }
    if (data.deposit !== undefined && data.deposit !== null && data.deposit !== '') {
      const depositValue = typeof data.deposit === 'string' 
        ? parseInt(data.deposit.replace(/,/g, ''), 10) 
        : parseInt(data.deposit, 10);
      if (!isNaN(depositValue)) {
        formattedData.deposit = depositValue * 10000; // 만원 단위를 원 단위로 변환
      }
    }
    if (data.contract_type) formattedData.contract_type = data.contract_type;
    if (data.start_date) formattedData.start_date = data.start_date;
    if (data.end_date) formattedData.end_date = data.end_date;
    
    // 선택적 필드들
    if (data.contract_number) formattedData.contract_number = data.contract_number;
    if (data.contract_category) formattedData.contract_category = data.contract_category;
    if (data.move_in_date) formattedData.move_in_date = data.move_in_date;
    if (data.move_out_date) formattedData.move_out_date = data.move_out_date;
    if (data.renewal_date) formattedData.renewal_date = data.renewal_date;
    if (data.contract_signed_date) formattedData.contract_signed_date = data.contract_signed_date;
    
    // 숫자 필드들
    if (data.maintenance_fee !== undefined && data.maintenance_fee !== '') {
      formattedData.maintenance_fee = parseInt(data.maintenance_fee);
    }
    if (data.parking_fee !== undefined && data.parking_fee !== '') {
      formattedData.parking_fee = parseInt(data.parking_fee);
    }
    if (data.max_occupants !== undefined && data.max_occupants !== '') {
      formattedData.max_occupants = parseInt(data.max_occupants);
    }
    if (data.parking_spaces !== undefined && data.parking_spaces !== '') {
      formattedData.parking_spaces = parseInt(data.parking_spaces);
    }
    if (data.due_date !== undefined && data.due_date !== '') {
      formattedData.due_date = parseInt(data.due_date);
    }
    if (data.late_fee_rate !== undefined && data.late_fee_rate !== '') {
      formattedData.late_fee_rate = parseFloat(data.late_fee_rate);
    }
    if (data.grace_period !== undefined && data.grace_period !== '') {
      formattedData.grace_period = parseInt(data.grace_period);
    }
    if (data.notice_period !== undefined && data.notice_period !== '') {
      formattedData.notice_period = parseInt(data.notice_period);
    }
    
    // 불린 필드들
    if (data.pet_allowed !== undefined) formattedData.pet_allowed = data.pet_allowed;
    if (data.smoking_allowed !== undefined) formattedData.smoking_allowed = data.smoking_allowed;
    if (data.utility_included !== undefined) formattedData.utility_included = data.utility_included;
    
    // 부가세 타입을 실제 부가세 필드로 변환
    const convertVatType = (vatType) => {
      return {
        included: { included: true, exempt: false, separate: false },
        exempt: { included: false, exempt: true, separate: false },
        separate: { included: false, exempt: false, separate: true }
      }[vatType] || { included: true, exempt: false, separate: false };
    };
    
    // 부가세 타입 변환 (항상 실행)
    const vatFieldsMonthly = convertVatType(monthlyRentVatType);
    formattedData.monthly_rent_vat_included = vatFieldsMonthly.included;
    formattedData.monthly_rent_vat_exempt = vatFieldsMonthly.exempt;
    formattedData.monthly_rent_vat_separate = vatFieldsMonthly.separate;
    console.log('🔍 월세 부가세 변환:', monthlyRentVatType, '->', vatFieldsMonthly);
    
    const vatFieldsMaintenance = convertVatType(maintenanceFeeVatType);
    formattedData.maintenance_fee_vat_included = vatFieldsMaintenance.included;
    formattedData.maintenance_fee_vat_exempt = vatFieldsMaintenance.exempt;
    formattedData.maintenance_fee_vat_separate = vatFieldsMaintenance.separate;
    console.log('🔍 관리비 부가세 변환:', maintenanceFeeVatType, '->', vatFieldsMaintenance);
    
    const vatFieldsParking = convertVatType(parkingFeeVatType);
    formattedData.parking_fee_vat_included = vatFieldsParking.included;
    formattedData.parking_fee_vat_exempt = vatFieldsParking.exempt;
    formattedData.parking_fee_vat_separate = vatFieldsParking.separate;
    console.log('🔍 주차비 부가세 변환:', parkingFeeVatType, '->', vatFieldsParking);
    
    // 기타 필드들
    if (data.status) formattedData.status = data.status;
    if (data.notes !== undefined) formattedData.notes = data.notes || null;
    
    // 제외할 필드들 제거
    excludedFields.forEach(field => {
      delete formattedData[field];
    });

    console.log('🔍 formattedData:', formattedData);
    console.log('🔍 formattedData JSON:', JSON.stringify(formattedData, null, 2));

    if (isEditMode) {
      console.log('🔍 수정 모드 - updateContractMutation 호출');
      updateContractMutation.mutate(formattedData);
    } else {
      console.log('🔍 등록 모드 - createContractMutation 호출');
      createContractMutation.mutate(formattedData);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedUnit(null);
    setSelectedTenant(null);
    setSelectedBuilding('');
    setCurrentStep(1);
    onClose();
  };

  // 단계별 진행 함수들
  const handleNextStep = () => {
    if (currentStep === 1) {
      // RMC 사용자인 경우 임대사업자 선택 검증
      if (isRMCUser && !selectedPropertyOwner) {
        toast.error('임대사업자를 선택해주세요.');
        return;
      }
      if (!selectedBuilding) {
        toast.error('건물을 선택해주세요.');
        return;
      }

      if (showNewTenantForm) {
        // 새 임차인 등록 모드에서는 폼 제출을 통해 다음 단계로 이동
        // handleTenantInfoSave에서 처리됨
        return;
      } else {
        if (!watch('tenant_id')) {
          toast.error('임차인을 선택해주세요.');
          return;
        }
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      if (!watch('unit_id')) {
        toast.error('세대를 선택해주세요.');
        return;
      }
      setCurrentStep(3);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return '1단계: 임대사업자/건물/임차인';
      case 2: return '2단계: 공실 선택';
      case 3: return '3단계: 계약 정보';
      default: return '계약 추가';
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 1: return '임대사업자와 건물을 선택한 뒤 임차인 정보를 입력하거나 기존 임차인을 선택해주세요.';
      case 2: return '임대할 세대를 선택해주세요.';
      case 3: return '계약 조건과 금액 정보를 입력해주세요.';
      default: return '';
    }
  };

  // 1단계: 임차인 정보 컴포넌트
  const TenantInfoStep = () => {
    // react-hook-form을 사용한 임차인 정보 입력
    const {
      register: registerTenant,
      handleSubmit: handleTenantSubmit,
      formState: { errors: tenantErrors },
      reset: resetTenant,
      watch: watchTenant
    } = useForm({
      defaultValues: {
        name: '',
        phone: '',
        email: '',
        resident_number: '',
        id_number: '',
        emergency_contact: '',
        emergency_phone: '',
        occupation: '',
        workplace: '',
        workplace_phone: '',
        notes: '',
        // 추가 필드들
        household_members: '',
        pet_ownership: false,
        special_agreement: false,
        vehicle_number: ''
      }
    });

    // 임차인 정보 저장
    const handleTenantInfoSave = (data) => {
      setNewTenantData(data);
      setCurrentStep(2); // 다음 단계로 이동
    };

    // 다음 단계 버튼 클릭 핸들러
    const handleNextStepClick = () => {
      // RMC 사용자인 경우 임대사업자 선택 검증
      if (isRMCUser && !selectedPropertyOwner) {
        toast.error('임대사업자를 선택해주세요.');
        return;
      }
      if (!selectedBuilding) {
        toast.error('건물을 선택해주세요.');
        return;
      }

      // 폼 유효성 검사
      const formData = watchTenant();

      if (!formData.name || !formData.phone || !formData.email) {
        toast.error('필수 정보(이름, 전화번호, 이메일)를 모두 입력해주세요.');
        return;
      }

      // 임차인 정보 저장
      setNewTenantData(formData);
      setCurrentStep(2); // 다음 단계로 이동
    };

    // 폼 제출 핸들러 (기본 동작 방지)
    const handleTenantFormSubmit = (e) => {
      e.preventDefault(); // 기본 제출 동작 방지
      handleTenantSubmit(handleTenantInfoSave)(e);
    };

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b pb-2">임차인 정보</h3>

        {/* 임대사업자 선택 (RMC 사용자용) */}
        {isRMCUser && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              임대사업자 선택 *
            </label>
            <select
              value={selectedPropertyOwner}
              onChange={(e) => setSelectedPropertyOwner(e.target.value)}
              className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">임대사업자 선택</option>
              {propertyOwners?.map((owner) => (
                <option key={owner.id} value={owner.id}>
                  {owner.company_name || owner.name} ({owner.buildings_count}개 건물)
                </option>
              ))}
            </select>
          </div>
        )}

        {/* 건물 선택 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            건물 선택 *
          </label>
          <select
            value={selectedBuilding}
            onChange={(e) => {
              setSelectedBuilding(e.target.value);
              setValue('unit_id', '');
              setSelectedUnit(null);
            }}
            disabled={isRMCUser && !selectedPropertyOwner}
            className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
          >
            <option value="">{isRMCUser && !selectedPropertyOwner ? '임대사업자를 먼저 선택하세요' : '건물 선택'}</option>
            {buildings.map((building) => (
              <option key={building.id} value={building.id}>
                {building.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="block text-sm font-medium text-gray-700">
              임차인 선택
            </label>
            <button
              type="button"
              onClick={() => setShowNewTenantForm(!showNewTenantForm)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showNewTenantForm ? '기존 임차인 선택' : '+ 새 임차인 등록'}
            </button>
          </div>

          {!showNewTenantForm ? (
            <>
              <select
                {...register('tenant_id')}
                className="w-full px-3 py-2 border rounded-md border-gray-300"
              >
                <option value="">임차인 선택</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} ({tenant.phone})
                  </option>
                ))}
              </select>
              {selectedTenant && (
                <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                  <div>이메일: {selectedTenant.email}</div>
                  <div>직업: {selectedTenant.occupation || '-'}</div>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 기본 정보 */}
                <div className="space-y-3">
                  <h4 className="text-md font-medium text-gray-800">기본 정보</h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      이름 *
                    </label>
                    <input
                      type="text"
                      {...registerTenant('name', { required: '이름을 입력해주세요.' })}
                      className={`w-full px-3 py-2 border rounded-md ${tenantErrors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="임차인 이름"
                    />
                    {tenantErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{tenantErrors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      전화번호 *
                    </label>
                    <input
                      type="tel"
                      {...registerTenant('phone', { required: '전화번호를 입력해주세요.' })}
                      className={`w-full px-3 py-2 border rounded-md ${tenantErrors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="010-1234-5678"
                    />
                    {tenantErrors.phone && (
                      <p className="mt-1 text-sm text-red-600">{tenantErrors.phone.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      이메일 *
                    </label>
                    <input
                      type="email"
                      {...registerTenant('email', {
                        required: '이메일을 입력해주세요.',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: '유효한 이메일 주소를 입력해주세요.'
                        }
                      })}
                      className={`w-full px-3 py-2 border rounded-md ${tenantErrors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="email@example.com"
                    />
                    {tenantErrors.email && (
                      <p className="mt-1 text-sm text-red-600">{tenantErrors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      주민등록번호
                    </label>
                    <input
                      type="text"
                      {...registerTenant('resident_number')}
                      className="w-full px-3 py-2 border rounded-md border-gray-300"
                      placeholder="000000-0000000"
                    />
                  </div>
                </div>

                {/* 추가 정보 */}
                <div className="space-y-3">
                  <h4 className="text-md font-medium text-gray-800">추가 정보</h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      직업
                    </label>
                    <input
                      type="text"
                      {...registerTenant('occupation')}
                      className="w-full px-3 py-2 border rounded-md border-gray-300"
                      placeholder="회사원"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      직장
                    </label>
                    <input
                      type="text"
                      {...registerTenant('workplace')}
                      className="w-full px-3 py-2 border rounded-md border-gray-300"
                      placeholder="회사명"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      비상연락처 이름
                    </label>
                    <input
                      type="text"
                      {...registerTenant('emergency_contact')}
                      className="w-full px-3 py-2 border rounded-md border-gray-300"
                      placeholder="부모님"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      비상연락처 전화번호
                    </label>
                    <input
                      type="tel"
                      {...registerTenant('emergency_phone')}
                      className="w-full px-3 py-2 border rounded-md border-gray-300"
                      placeholder="010-9876-5432"
                    />
                  </div>
                </div>
              </div>

              {/* 특이사항 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  특이사항
                </label>
                <textarea
                  {...registerTenant('notes')}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-md border-gray-300"
                  placeholder="특이사항이 있다면 입력해주세요."
                />
              </div>

              {/* 추가 정보 섹션 */}
              <div className="space-y-3">
                <h4 className="text-md font-medium text-gray-800">거주 및 차량 정보</h4>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    거주인원
                  </label>
                  <input
                    type="number"
                    {...registerTenant('household_members')}
                    className="w-full px-3 py-2 border rounded-md border-gray-300"
                    placeholder="1"
                    min="1"
                    max="10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    차량번호
                  </label>
                  <input
                    type="text"
                    {...registerTenant('vehicle_number')}
                    className="w-full px-3 py-2 border rounded-md border-gray-300"
                    placeholder="12가3456"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      {...registerTenant('pet_ownership')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">
                      반려동물 보유
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      {...registerTenant('special_agreement')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">
                      특약사항 동의
                    </label>
                  </div>
                </div>
              </div>

              {/* 다음 단계 버튼 */}
              <div className="flex justify-end pt-4">
                <button
                  type="button"
                  onClick={handleNextStepClick}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  다음 단계
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // 2단계: 공실 선택 컴포넌트
  const UnitSelectionStep = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 border-b pb-2">공실 선택</h3>
      <div className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800">
        선택한 건물의 임대 가능 세대만 표시됩니다.
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          세대 선택 *
        </label>
        <select
          {...register('unit_id')}
          className="w-full px-3 py-2 border rounded-md border-gray-300"
        >
          <option value="">{selectedBuilding ? '세대 선택' : '건물을 먼저 선택하세요'}</option>
          {units
            .filter(unit => unit.status === 'available') // 사용 가능한 세대만 필터링
            .map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.building_name} - {unit.unit_number}호 ({unit.unit_type}, {unit.area}㎡)
              </option>
            ))}
        </select>
        {selectedUnit && (
          <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
            <div>기준월세: {selectedUnit.standard_monthly_rent?.toLocaleString()}원</div>
            <div>기준보증금: {selectedUnit.standard_deposit?.toLocaleString()}원</div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isViewMode ? '계약 상세보기' : isEditMode ? '계약 수정' : getStepTitle()}
            </h2>
            {!isViewMode && !isEditMode && (
              <p className="text-sm text-gray-600 mt-1">{getStepDescription()}</p>
            )}
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* 단계별 진행 표시 */}
        {!isViewMode && !isEditMode && (
          <div className="px-6 py-4 bg-gray-50 border-b">
            <div className="flex items-center justify-center space-x-4">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step === currentStep
                      ? 'bg-blue-600 text-white'
                      : step < currentStep
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-300 text-gray-600'
                    }`}>
                    {step < currentStep ? '✓' : step}
                  </div>
                  {step < 3 && (
                    <div className={`w-12 h-1 mx-2 ${step < currentStep ? 'bg-green-500' : 'bg-gray-300'
                      }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* 단계별 내용 표시 */}
          {(!isViewMode && !isEditMode && currentStep === 1) && <TenantInfoStep />}
          {(!isViewMode && !isEditMode && currentStep === 2) && <UnitSelectionStep />}

          {/* 3단계 또는 수정/보기 모드: 전체 계약 정보 */}
          {((isViewMode || isEditMode) || (!isViewMode && !isEditMode && currentStep === 3)) && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 계약 기본 정보 */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b pb-2">계약 정보</h3>

                  {/* 계약번호는 자동생성되므로 제거 */}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      계약유형 *
                    </label>
                    <select
                      {...register('contract_type', { required: '계약유형을 선택해주세요.' })}
                      disabled={isViewMode}
                      className={`w-full px-3 py-2 border rounded-md ${errors.contract_type ? 'border-red-500' : 'border-gray-300'
                        } ${isViewMode ? 'bg-gray-100' : ''}`}
                    >
                      <option value="">계약유형 선택</option>
            <option value="monthly">월세</option>
            <option value="jeonse">전세제</option>
            <option value="deposit">삭월세</option>
            <option value="yearly">연세</option>
                    </select>
                    {errors.contract_type && (
                      <p className="mt-1 text-sm text-red-600">{errors.contract_type.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      계약분류
                    </label>
                    <select
                      {...register('contract_category')}
                      disabled={isViewMode}
                      className={`w-full px-3 py-2 border rounded-md border-gray-300 ${isViewMode ? 'bg-gray-100' : ''
                        }`}
                    >
                      <option value="">계약분류 선택</option>
                      <option value="residential">주거용</option>
                      <option value="commercial">상업용</option>
                      <option value="office">사무용</option>
                    </select>
                  </div>

                  {/* 계약기간 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      계약기간 (개월)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="120"
                      {...register('contract_period')}
                      onChange={(e) => handleContractPeriodChange(e.target.value)}
                      disabled={isViewMode}
                      className={`w-full px-3 py-2 border rounded-md border-gray-300 ${isViewMode ? 'bg-gray-100' : ''
                        }`}
                      placeholder="12"
                    />
                  </div>

                  {/* 계약시작일과 계약종료일 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        계약시작일 *
                      </label>
                      <input
                        type="date"
                        {...register('start_date', { required: '계약시작일을 입력해주세요.' })}
                        onChange={handleStartDateChange}
                        disabled={isViewMode}
                        className={`w-full px-3 py-2 border rounded-md ${errors.start_date ? 'border-red-500' : 'border-gray-300'
                          } ${isViewMode ? 'bg-gray-100' : ''}`}
                      />
                      {errors.start_date && (
                        <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        계약종료일 *
                      </label>
                      <input
                        type="date"
                        {...register('end_date', { required: '계약종료일을 입력해주세요.' })}
                        disabled={isViewMode}
                        className={`w-full px-3 py-2 border rounded-md ${errors.end_date ? 'border-red-500' : 'border-gray-300'
                          } ${isViewMode ? 'bg-gray-100' : ''}`}
                      />
                      {errors.end_date && (
                        <p className="mt-1 text-sm text-red-600">{errors.end_date.message}</p>
                      )}
                    </div>
                  </div>

                  {/* 입주일과 퇴주예정일 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        입주일
                      </label>
                      <input
                        type="date"
                        {...register('move_in_date')}
                        onChange={handleMoveInDateChange}
                        disabled={isViewMode}
                        className={`w-full px-3 py-2 border rounded-md border-gray-300 ${isViewMode ? 'bg-gray-100' : ''
                          }`}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        퇴주예정일
                      </label>
                      <input
                        type="date"
                        {...register('move_out_date')}
                        disabled={isViewMode}
                        className={`w-full px-3 py-2 border rounded-md border-gray-300 ${isViewMode ? 'bg-gray-100' : ''
                          }`}
                      />
                    </div>
                  </div>

                  {/* 갱신일과 해지통보기간 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        갱신일
                      </label>
                      <input
                        type="date"
                        {...register('renewal_date')}
                        disabled={isViewMode}
                        className={`w-full px-3 py-2 border rounded-md border-gray-300 ${isViewMode ? 'bg-gray-100' : ''
                          }`}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        해지통보기간 (일)
                      </label>
                      <input
                        type="number"
                        {...register('notice_period', {
                          min: { value: 1, message: '최소 1일 이상이어야 합니다.' }
                        })}
                        disabled={isViewMode}
                        className={`w-full px-3 py-2 border rounded-md border-gray-300 ${isViewMode ? 'bg-gray-100' : ''
                          }`}
                      />
                    </div>
                  </div>
                </div>

                {/* 금액 정보 */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 border-b pb-2">금액 정보</h3>

                  {/* 월세 */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        월세 (만원) *
                      </label>
                      <Controller
                        name="monthly_rent_vat_type"
                        control={control}
                        render={({ field }) => (
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center">
                              <input
                                type="radio"
                                {...field}
                                value="included"
                                checked={field.value === 'included'}
                                disabled={isViewMode}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <label className="ml-2 block text-sm text-gray-700">
                                부가세 포함
                              </label>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                {...field}
                                value="separate"
                                checked={field.value === 'separate'}
                                disabled={isViewMode}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <label className="ml-2 block text-sm text-gray-700">
                                부가세 별도
                              </label>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                {...field}
                                value="exempt"
                                checked={field.value === 'exempt'}
                                disabled={isViewMode}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <label className="ml-2 block text-sm text-gray-700">
                                부가세 면제
                              </label>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                    <input
                      type="number"
                      {...register('monthly_rent', {
                        required: '월세를 입력해주세요.',
                        min: { value: 0, message: '월세는 0 이상이어야 합니다.' }
                      })}
                      disabled={isViewMode}
                      className={`w-full px-3 py-2 border rounded-md ${errors.monthly_rent ? 'border-red-500' : 'border-gray-300'
                        } ${isViewMode ? 'bg-gray-100' : ''}`}
                      placeholder="월세 금액 (만원)"
                    />
                    {errors.monthly_rent && (
                      <p className="mt-1 text-sm text-red-600">{errors.monthly_rent.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      보증금 (만원) *
                    </label>
                    <input
                      type="number"
                      {...register('deposit', {
                        required: '보증금을 입력해주세요.',
                        min: { value: 0, message: '보증금은 0 이상이어야 합니다.' }
                      })}
                      disabled={isViewMode}
                      className={`w-full px-3 py-2 border rounded-md ${errors.deposit ? 'border-red-500' : 'border-gray-300'
                        } ${isViewMode ? 'bg-gray-100' : ''}`}
                      placeholder="보증금 금액 (만원)"
                    />
                    {errors.deposit && (
                      <p className="mt-1 text-sm text-red-600">{errors.deposit.message}</p>
                    )}
                  </div>

                  {/* 관리비 */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        관리비
                      </label>
                      <Controller
                        name="maintenance_fee_vat_type"
                        control={control}
                        render={({ field }) => (
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center">
                              <input
                                type="radio"
                                {...field}
                                value="included"
                                checked={field.value === 'included'}
                                disabled={isViewMode}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <label className="ml-2 block text-sm text-gray-700">
                                부가세 포함
                              </label>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                {...field}
                                value="separate"
                                checked={field.value === 'separate'}
                                disabled={isViewMode}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <label className="ml-2 block text-sm text-gray-700">
                                부가세 별도
                              </label>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                {...field}
                                value="exempt"
                                checked={field.value === 'exempt'}
                                disabled={isViewMode}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <label className="ml-2 block text-sm text-gray-700">
                                부가세 면제
                              </label>
                            </div>
                          </div>
                        )}
                      />
                    </div>

                    {/* 관리비 금액 입력 */}
                    <input
                      type="number"
                      {...register('maintenance_fee', {
                        min: { value: 0, message: '관리비는 0 이상이어야 합니다.' }
                      })}
                      disabled={isViewMode}
                      className="w-full px-3 py-2 border rounded-md border-gray-300"
                      placeholder="월정액 관리비"
                    />
                  </div>

                  {/* 주차비 */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        주차비
                      </label>
                      <Controller
                        name="parking_fee_vat_type"
                        control={control}
                        render={({ field }) => (
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center">
                              <input
                                type="radio"
                                {...field}
                                value="included"
                                checked={field.value === 'included'}
                                disabled={isViewMode}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <label className="ml-2 block text-sm text-gray-700">
                                부가세 포함
                              </label>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                {...field}
                                value="separate"
                                checked={field.value === 'separate'}
                                disabled={isViewMode}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <label className="ml-2 block text-sm text-gray-700">
                                부가세 별도
                              </label>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                {...field}
                                value="exempt"
                                checked={field.value === 'exempt'}
                                disabled={isViewMode}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <label className="ml-2 block text-sm text-gray-700">
                                부가세 면제
                              </label>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                    <input
                      type="number"
                      {...register('parking_fee', {
                        min: { value: 0, message: '주차비는 0 이상이어야 합니다.' }
                      })}
                      disabled={isViewMode}
                      className="w-full px-3 py-2 border rounded-md border-gray-300"
                      placeholder="주차비 금액"
                    />
                  </div>

                  {/* 통합 납부일 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      납부일 (매월 N일) *
                    </label>
                    <input
                      type="number"
                      {...register('due_date', {
                        required: '납부일을 입력해주세요.',
                        min: { value: 1, message: '납부일은 1일 이상이어야 합니다.' },
                        max: { value: 31, message: '납부일은 31일 이하여야 합니다.' }
                      })}
                      disabled={isViewMode}
                      className={`w-full px-3 py-2 border rounded-md ${errors.due_date ? 'border-red-500' : 'border-gray-300'
                        } ${isViewMode ? 'bg-gray-100' : ''}`}
                      placeholder="예: 1 (매월 1일)"
                    />
                    {errors.due_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.due_date.message}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      월세, 관리비, 주차비 모두 동일한 날짜에 납부됩니다.
                    </p>
                  </div>
                </div>
              </div>

              {/* 특이사항 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  특이사항
                </label>
                <textarea
                  {...register('notes')}
                  disabled={isViewMode}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md border-gray-300"
                  placeholder="계약 관련 특이사항이 있다면 입력해주세요."
                />
              </div>
            </div>
          )}

          {/* 버튼 */}
          {!isViewMode && (
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                취소
              </button>

              {!isEditMode && currentStep < 3 && (
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  다음 단계
                </button>
              )}

              {!isEditMode && currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  이전 단계
                </button>
              )}

              {((isEditMode) || (!isEditMode && currentStep === 3)) && (
                <button
                  type="submit"
                  disabled={isSubmitting || updateContractMutation.isLoading || createContractMutation.isLoading}
                  onClick={(e) => {
                    console.log('🔍 수정 버튼 클릭됨');
                    console.log('🔍 isEditMode:', isEditMode);
                    console.log('🔍 isSubmitting:', isSubmitting);
                    console.log('🔍 updateContractMutation.isLoading:', updateContractMutation.isLoading);
                    console.log('🔍 createContractMutation.isLoading:', createContractMutation.isLoading);
                    // form submit은 자동으로 처리됨
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting || updateContractMutation.isLoading || createContractMutation.isLoading 
                    ? '처리 중...' 
                    : isEditMode 
                      ? '수정' 
                      : '계약 등록'}
                </button>
              )}
            </div>
          )}

          {isViewMode && (
            <div className="flex justify-end pt-6 border-t">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                닫기
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ContractModal;
